import { useMemo, useState } from 'react';
import { Building2, Cpu } from 'lucide-react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { NoReadAccessState } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { useAgentModelPolicy } from '@/hooks/config/useAgentModelPolicy';
import { useAuth } from '@/hooks/auth/useAuth';
import { LaunchDefaultsPanel } from './LaunchDefaultsPanel';
import { AgentPolicyCard } from './AgentPolicyCard';
import { SingleModelAgentCard } from './SingleModelAgentCard';
import { ApiError } from '@/lib/api';

const MANAGE_DESCRIPTION =
  'Enable or disable agent types, choose provider/model availability for multi-model agents, and set defaults for the Launch dialog.';
const VIEW_DESCRIPTION =
  'View agent type availability, provider/model configuration, and Launch dialog defaults for this organization.';

export function OrganizationAgentModelsPage({ embedded = false }: { embedded?: boolean }) {
  const { activeOrganizationId, authEnabled, isPlatformAdmin, can } = useAuth();
  const canViewAgentModels = !authEnabled || isPlatformAdmin || can('org:read');
  const canManageAgentModels = !authEnabled || isPlatformAdmin || can('org:manage');
  const readOnly = !canManageAgentModels;
  const policyQuery = useAgentModelPolicy({
    enabled: Boolean(activeOrganizationId) && canViewAgentModels,
  });

  const agentModelsHeader = {
    iconVariant: 'box' as const,
    icon: <Cpu className="h-5 w-5 text-muted-foreground" />,
    title: 'Agent Models',
    description: readOnly ? VIEW_DESCRIPTION : MANAGE_DESCRIPTION,
    docs: 'administration/agent-model-configuration',
  };

  const errorMessage = useMemo(() => {
    if (!policyQuery.error) return undefined;
    const err = policyQuery.error;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        return 'Authentication required. Please sign in to access this page.';
      }
      if (err.status === 403) {
        return 'Access denied. You do not have permission to view agent model policies.';
      }
    }
    return 'Failed to load agent model policy';
  }, [policyQuery.error]);

  const adminData = policyQuery.data;
  const entries = adminData?.agents ?? [];
  const launchableAgents = adminData?.launchableAgents ?? [];
  const multiModelTypes = new Set(entries.map((e) => e.agentType));
  // Agents that are registered but have no multi-model config (PRD-24: these
  // still need an enable/disable toggle even though there's no provider card).
  const singleModelLaunchable = launchableAgents.filter((a) => !multiModelTypes.has(a.agentType));

  // Snapshot the enabled-first order on first non-empty load so toggles do not
  // re-sort the list mid-session. Agent types that appear later (rare; e.g. a
  // newly registered plugin) fall through to the end via `Infinity`. Setting
  // state during render is the documented React pattern for deriving cached
  // state from props (https://react.dev/reference/react/useState#storing-information-from-previous-renders);
  // React discards this render and re-renders synchronously with the populated map.
  const [initialOrder, setInitialOrder] = useState<Map<string, number> | null>(null);
  if (initialOrder === null && (entries.length > 0 || launchableAgents.length > 0)) {
    const enabledLookup = new Map(launchableAgents.map((a) => [a.agentType, a.effectiveEnabled]));
    const isEnabled = (agentType: string) => enabledLookup.get(agentType) ?? true;
    const seenOrder = new Map<string, number>();
    const orderedTypes = [
      ...[...entries].sort(
        (a, b) => Number(isEnabled(b.agentType)) - Number(isEnabled(a.agentType)),
      ),
      ...[...singleModelLaunchable].sort(
        (a, b) => Number(b.effectiveEnabled) - Number(a.effectiveEnabled),
      ),
    ].map((item) => item.agentType);
    for (const agentType of orderedTypes) {
      if (!seenOrder.has(agentType)) {
        seenOrder.set(agentType, seenOrder.size);
      }
    }
    setInitialOrder(seenOrder);
  }

  const orderIndex = (agentType: string): number =>
    initialOrder?.get(agentType) ?? Number.POSITIVE_INFINITY;

  const sortedEntries = [...entries]
    .map((entry, idx) => ({ entry, idx }))
    .sort((a, b) => {
      const diff = orderIndex(a.entry.agentType) - orderIndex(b.entry.agentType);
      return diff !== 0 ? diff : a.idx - b.idx;
    })
    .map(({ entry }) => entry);
  const sortedSingleModel = [...singleModelLaunchable]
    .map((agent, idx) => ({ agent, idx }))
    .sort((a, b) => {
      const diff = orderIndex(a.agent.agentType) - orderIndex(b.agent.agentType);
      return diff !== 0 ? diff : a.idx - b.idx;
    })
    .map(({ agent }) => agent);

  const noOrgBody = (
    <div
      className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed p-12 text-center"
      data-testid="no-org-state"
    >
      <Building2 className="h-12 w-12 text-muted-foreground/50" />
      <div className="space-y-1">
        <p className="text-sm font-medium">No organization selected</p>
        <p className="text-sm text-muted-foreground">
          Select an organization to view agent model settings.
        </p>
      </div>
    </div>
  );

  const accessDeniedBody = <NoReadAccessState permission="org:read" />;

  const policyBody = (
    <AsyncContent
      isLoading={policyQuery.isLoading}
      isError={policyQuery.isError}
      errorMessage={errorMessage}
      loadingMessage="Loading agent models…"
    >
      {adminData && (
        <div className="space-y-6">
          <LaunchDefaultsPanel scope="org" adminData={adminData} readOnly={readOnly} />

          {sortedEntries.map((entry) => {
            const launchable = launchableAgents.find((a) => a.agentType === entry.agentType);
            return (
              <div
                key={entry.agentType}
                data-testid="agent-models-row"
                data-agent-type={entry.agentType}
              >
                <AgentPolicyCard
                  entry={entry}
                  scope="org"
                  launchable={launchable}
                  readOnly={readOnly}
                />
              </div>
            );
          })}

          {sortedSingleModel.map((agent) => (
            <div
              key={agent.agentType}
              data-testid="agent-models-row"
              data-agent-type={agent.agentType}
            >
              <SingleModelAgentCard agent={agent} scope="org" readOnly={readOnly} />
            </div>
          ))}

          {sortedEntries.length === 0 && sortedSingleModel.length === 0 && (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No multi-model agents registered. Launch defaults above still apply to all agent
              types.
            </div>
          )}
        </div>
      )}
    </AsyncContent>
  );

  if (!activeOrganizationId) {
    if (embedded) return noOrgBody;
    return (
      <PageLayout maxWidth="4xl">
        <PageHeader {...agentModelsHeader} />
        {noOrgBody}
      </PageLayout>
    );
  }

  if (!canViewAgentModels) {
    if (embedded) return accessDeniedBody;
    return <PageLayout maxWidth="4xl">{accessDeniedBody}</PageLayout>;
  }

  if (embedded) {
    // Embedded inside the Agents hub, which already renders the "Agents"
    // title and the Personas / Agent Models section rail. Omit the section
    // title — keep description and docs only.
    return (
      <div className="space-y-5">
        <PageHeader description={agentModelsHeader.description} docs={agentModelsHeader.docs} />
        {policyBody}
      </div>
    );
  }

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader {...agentModelsHeader} />
      {policyBody}
    </PageLayout>
  );
}
